import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { formatDate } from "../../../../utils/formatters";
import { adminDataTableHeadSx } from "../../../../utils/adminTableScrollSx";
import { usePermissions } from "../../../Context/permissionsProvider";
import ContentSectionCard from "../../content/ContentSectionCard";
import { contentTablePaginationFooterSx } from "../../content/contentEditorStyles";
import NewsletterHistoryStatusChip from "./NewsletterHistoryStatusChip";
import CancelIcon from "@mui/icons-material/Cancel";

const historyTableHeadSx = {
  ...adminDataTableHeadSx,
  "& .MuiTableRow-head .MuiTableCell-head:first-of-type": {
    borderTopLeftRadius: 12,
  },
  "& .MuiTableRow-head .MuiTableCell-head:last-of-type": {
    borderTopRightRadius: 12,
  },
};

const historyTableSx = {
  width: "100%",
  tableLayout: "fixed",
};

const subjectCellSx = {
  py: 0.75,
  maxWidth: 0,
  width: "36%",
  overflow: "hidden",
};

const subjectTextSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
  minWidth: 0,
};

const NewsletterHistoryListPanel = ({
  items,
  count,
  page,
  pageSize,
  onPageChange,
  onShowDetail,
  onCancelScheduled,
  cancellingId,
  loading,
}) => {
  const { isActionVisible } = usePermissions();
  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  return (
    <ContentSectionCard noPadding sx={{ width: "100%" }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Table sx={historyTableSx}>
          <TableHead sx={historyTableHeadSx}>
            <TableRow>
              <TableCell sx={{ py: 1, width: "36%", maxWidth: 0 }}>Oggetto</TableCell>
              <TableCell sx={{ py: 1, whiteSpace: "nowrap", width: "12%" }}>Stato</TableCell>
              <TableCell sx={{ py: 1, whiteSpace: "nowrap", width: "22%" }}>
                Inviato / programmato il
              </TableCell>
              <TableCell sx={{ py: 1, whiteSpace: "nowrap", width: "14%" }}>Creato</TableCell>
              <TableCell align="right" sx={{ py: 1, whiteSpace: "nowrap", width: "16%" }}>
                Azioni
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nessuna pubblicazione trovata con i filtri correnti.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item, index) => (
                <TableRow key={item.id} sx={{ backgroundColor: index % 2 === 0 ? "white" : "#f8fafc" }}>
                  <TableCell sx={subjectCellSx}>
                    <Tooltip title={item.subject || ""}>
                      <Typography variant="body2" sx={subjectTextSx}>
                        {item.subject}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ py: 0.75, whiteSpace: "nowrap" }}>
                    <NewsletterHistoryStatusChip status={item.status} />
                  </TableCell>
                  <TableCell sx={{ py: 0.75, whiteSpace: "nowrap" }}>
                    {item.sentAt ? formatDate(item.sentAt) : "—"}
                  </TableCell>
                  <TableCell sx={{ py: 0.75, whiteSpace: "nowrap" }}>{formatDate(item.createdAt)}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, whiteSpace: "nowrap" }}>
                    
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                    {item.status === "scheduled" && isActionVisible("newsletterStorico", "annullaProgrammato") ? (
                        <Tooltip title="Annulla l'invio programmato di questa newsletter">
                          <span>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              disabled={cancellingId === item.id}
                              onClick={() => onCancelScheduled?.(item)}
                              sx={{ minWidth: 0, px: 1 }}
                            >
                              <CancelIcon fontSize="small" />
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null}
                      {isActionVisible("newsletterStorico", "dettagli") && (
                        <Tooltip title="Visualizza i dettagli della pubblicazione">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => onShowDetail(item.id)}
                              sx={{ minWidth: 0, px: 1 }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
      {count > pageSize ? (
        <Box sx={contentTablePaginationFooterSx}>
          <Pagination count={totalPages} page={page + 1} onChange={(_, value) => onPageChange(value - 1)} size="small" />
        </Box>
      ) : null}
    </ContentSectionCard>
  );
};

NewsletterHistoryListPanel.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  count: PropTypes.number,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onShowDetail: PropTypes.func.isRequired,
  onCancelScheduled: PropTypes.func,
  cancellingId: PropTypes.number,
  loading: PropTypes.bool,
};

export default NewsletterHistoryListPanel;
